import * as React from "react";
import {authorize, IWithRouterProps, Overlay, withRouter} from "@athena-portal/components";
import {PluginTemplates} from "../../../../../utils/PluginTemplates";
import FalconFooter from "../../../../../components/navigation/FalconFooter";
import FalconHeader from "../../../../../components/navigation/FalconHeader";
import {ClaimDatePicker} from "../../../../../components/claims/ClaimDatePicker";
import {
    deleteSessionStorage,
    getMotorPoliciesMinDate,
    getMtcEndDate,
    getSessionStorage,
    scrollToTop,
    setSessionStorage
} from "../../../../../CommonFunctions";
import {ICustomerPortalService, OnlineMotorClaimRequest, Policy, ProductsEnum, Types, FalconNavigatorHeader, POLICY_STATUS_NEW} from "@falcon/services";
import {lazyInject} from "@athena-portal/core";
import * as moment from "moment";
import {BackButton} from "../../../../../components/navigation/BackButton";
import {NextButton} from "../../../subscription/motor/motorClaims/NextButton";
import {IUrgentClaim} from "../../../subscription/motor/motorClaims/UrgentClaimPage";
import {TextAreaComponent} from "../../../../../components/inputs/TextAreaComponent";
import {AllianzOnlineBadge} from "../../../../../components/AllianzOnlineBadge";

interface IWhenDidItHappenPageState {
    onlineMotorClaimRequest: OnlineMotorClaimRequest;
    whenDidItHappen: IWhenDidItHappen;
    urgentClaim: IUrgentClaim;
}

export interface IWhenDidItHappen {
    isAccidentHourValid: boolean;
    isClaimReasonDescriptionValid: boolean;
    policyHistory?: Policy[];
}

@authorize
export class WhenDidAccidentHappenPagePlain extends React.Component<IWithRouterProps, IWhenDidItHappenPageState> {

    private pluginTemplates = new PluginTemplates();

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    constructor(props: IWithRouterProps) {
        super(props);
        const onlineMotorClaimRequest: OnlineMotorClaimRequest = getSessionStorage("onlineMotorClaimRequest");
        const whenDidItHappen: IWhenDidItHappen = getSessionStorage("whenDidItHappen");
        const urgentClaim: IUrgentClaim = getSessionStorage("urgentClaim");

        this.state = {
            onlineMotorClaimRequest: onlineMotorClaimRequest !== null ? onlineMotorClaimRequest : new OnlineMotorClaimRequest(),
            whenDidItHappen: whenDidItHappen !== null ? whenDidItHappen : {
                policyHistory: [],
                isAccidentHourValid: true,
                isClaimReasonDescriptionValid: true
            } as IWhenDidItHappen,
            urgentClaim: urgentClaim !== null ? urgentClaim : {
                isUrgentClaim: null,
                showUrgentClaimPopup: false
            } as IUrgentClaim
        }
    };

    public componentDidMount(): void {
        if (!this.isUserInformationPresent()){
            this.props.navigate("/dashboard/otherClaims/coverDetails", {state: {showOverlay: true}});
        }

        scrollToTop();
        this.pluginTemplates.setPageTitle("Make a car claim");

        const whenDidItHappen: IWhenDidItHappen = {...this.state.whenDidItHappen};
        this.customerPortalService.getPolicyHistory().subscribe((policies: Policy[]) => {
            whenDidItHappen.policyHistory = policies;
            this.setState({whenDidItHappen});
            setSessionStorage("whenDidItHappen", whenDidItHappen);
        });
    }

    public render() {
        const {onlineMotorClaimRequest, whenDidItHappen} = this.state;

        if (whenDidItHappen.policyHistory && whenDidItHappen.policyHistory.length > 0) {
            return (
                <React.Fragment>
                    <FalconHeader/>

                    <div className="page-contents">
                        <FalconNavigatorHeader
                            back="/dashboard/windscreen/urgent/coverDetails"
                            title="What happened?"
                        />
                        <AllianzOnlineBadge/>
                        {this.renderWhenAndWherePanel()}
                        <div className="section-panel">
                            <TextAreaComponent
                               questionLabel={"Give us a short description of what happened. Include the cause of your claim"}
                               isValid={whenDidItHappen.isClaimReasonDescriptionValid}
                               onChange={this.handleClaimReasonDescription}
                               id={"shortDescriptionOfClaim"}
                               maxLength={300}
                               value={onlineMotorClaimRequest.claimingReasonDescription}
                               largeTextArea={false}
                            />
                        </div>
                        <NextButton buttonId={"submitWhenDidItHappenPage"}
                                    disabled={!this.isFormValid()}
                                    onClick={this.handleSubmit}/>
                        <BackButton isTop={false} link={"/dashboard/windscreen/urgent/coverDetails"} />
                    </div>

                    <FalconFooter/>
                </React.Fragment>
            )
        } else {
            return (<Overlay show={true}/>)
        }
    }

    private isFormValid = (): boolean => {
        const onlineMotorClaimRequest: OnlineMotorClaimRequest = {...this.state.onlineMotorClaimRequest};
        const urgentClaim: IUrgentClaim = {...this.state.urgentClaim};

        const isTimeValid: boolean = onlineMotorClaimRequest.accidentDate !== undefined;

        const isOtherInformationValid: boolean = onlineMotorClaimRequest.claimingReasonDescription !== undefined &&
            onlineMotorClaimRequest.claimingReasonDescription !== "" && urgentClaim.isUrgentClaim !== undefined;

        return isTimeValid && isOtherInformationValid && this.isUserInformationPresent();
    };

    private isUserInformationPresent = (): boolean => {
        const onlineMotorClaimRequest: OnlineMotorClaimRequest = {...this.state.onlineMotorClaimRequest};

        return !(!onlineMotorClaimRequest.dateOfBirth ||
            !onlineMotorClaimRequest.contactEmail ||
            !onlineMotorClaimRequest.firstName ||
            !onlineMotorClaimRequest.lastName ||
            !onlineMotorClaimRequest.fullAddress ||
            !onlineMotorClaimRequest.contactMobile);
    };

    private renderWhenAndWherePanel = (): React.ReactNode => {
        const motorPoliciesMinDate: string = getMotorPoliciesMinDate(this.state.whenDidItHappen.policyHistory!, ProductsEnum.WOLF_MOTOR, POLICY_STATUS_NEW);

        const mtcEndDate = getMtcEndDate(this.state.whenDidItHappen.policyHistory!, ProductsEnum.WOLF_MOTOR);
        const motorPolicyMtcEndDate: string | null = moment(motorPoliciesMinDate).isAfter(mtcEndDate) ? null : mtcEndDate;

        return (
            <React.Fragment>
                <div className="section-panel pb-2">
                    <div className="form-group">
                        <label htmlFor="accidentDate">When did the damage happened?</label>
                        <ClaimDatePicker onDateSelect={this.handleAccidentDate}
                                         date={this.state.onlineMotorClaimRequest.accidentDate}
                                         minDate={motorPoliciesMinDate}
                                         mtcMaxDate={motorPolicyMtcEndDate}/>
                    </div>
                </div>
            </React.Fragment>
        )
    };

    private handleSubmit = (event: any): void => {
        if (event.target.disabled) {
            event.preventDefault();
        } else {
            const onlineMotorClaimRequest: OnlineMotorClaimRequest = {...this.state.onlineMotorClaimRequest};
            onlineMotorClaimRequest.claimSubmissionDate = moment().format("YYYY-MM-DD HH:mm");
            this.setState({onlineMotorClaimRequest}, () => {
                // this.customerPortalService.createOnlineMotorClaimRequest(this.state.onlineMotorClaimRequest).subscribe(); TODO: Wolf claims email sending implementation
                this.clearSessionStorage();
                this.props.navigate("/dashboard/otherClaims/coverDetails/occur/success", {state: {showOverlay: true}});
            });
        }
    };

    private clearSessionStorage = (): void => {
        deleteSessionStorage("urgentClaim");
        deleteSessionStorage("whenDidItHappen");
        deleteSessionStorage("policyDetails");
        deleteSessionStorage("contactDetails");
    };

    private handleClaimReasonDescription = (event: any): void => {
        const state: string = event.target.value;
        const onlineMotorClaimRequest: OnlineMotorClaimRequest = {...this.state.onlineMotorClaimRequest};
        const whenDidItHappen: IWhenDidItHappen = {...this.state.whenDidItHappen!};
        onlineMotorClaimRequest.claimingReasonDescription = state;
        whenDidItHappen.isClaimReasonDescriptionValid = state !== "";
        this.setState({onlineMotorClaimRequest, whenDidItHappen});
        setSessionStorage("onlineMotorClaimRequest", onlineMotorClaimRequest);
        setSessionStorage("whenDidItHappen", whenDidItHappen);
    };

    private handleAccidentDate = (state: string): void => {
        const onlineMotorClaimRequest: OnlineMotorClaimRequest = {...this.state.onlineMotorClaimRequest};
        onlineMotorClaimRequest.accidentDate = state;
        onlineMotorClaimRequest.accidentHourTime = "";
        onlineMotorClaimRequest.accidentHour = "";
        this.setState({onlineMotorClaimRequest});
        setSessionStorage("onlineMotorClaimRequest", onlineMotorClaimRequest);
    };
}

export const WhenDidAccidentHappenPage = withRouter(WhenDidAccidentHappenPagePlain)
